import { getCSS, remCSS } from '@gimlite/common';
import classNames from 'classnames';
import React, { memo, useId, useMemo, useState } from 'react';
import { useDevice } from '../../hooks/useDevice.hook';
import { ButtonRounded } from '../buttonRounded/buttonRounded.component';
import { Event } from '../event/event.component';
import { Icon, IconType } from '../icon/icon.component';
import { Text } from '../text/text.component';
import './description.component.scss';

export declare namespace DescriptionType {
  type Props = {
    className?: string | null;
    label?: string | null;
    value: string | React.ReactNode | null;
    extra?: React.ReactNode[];
    columnExtend?: number;
    enableEmptyText?: boolean;
    copyable?: boolean;
  };
}

const DescriptionComponent = ({
  label,
  value,
  className,
  extra,
  columnExtend = 1,
  enableEmptyText = true,
  copyable = false,
}: DescriptionType.Props) => {
  return (
    <div
      className={classNames(`description`, className)}
      style={{
        gridColumn: `span ${columnExtend}`,
      }}
    >
      <div
        style={{
          gap: typeof value === 'string' ? '0px' : remCSS(4),
        }}
        className="description__content"
      >
        {label && (
          <Text variant="label" textTransform="uppercase" color="black_50">
            {label || '‎'}
          </Text>
        )}
        {value === null ||
        value === undefined ||
        typeof value === 'string' ||
        typeof value === 'number' ? (
          <div className="description__content__value">
            {(typeof value === 'string' || typeof value === 'number') &&
            value !== '' ? (
              <Text
                wordBreak="break-all"
                textOverflow="ellipsis"
                variant="subhead"
                color={'black_90'}
              >
                {value}
              </Text>
            ) : enableEmptyText ? (
              <Text
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                variant="subhead"
                color={'black_50'}
              >
                N/A
              </Text>
            ) : (
              <Text
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                variant="subhead"
                color={'black_90'}
              >
                ‎
              </Text>
            )}

            {!!value && copyable && (
              <Event
                onClick={() => {
                  navigator.clipboard.writeText(value as string);
                }}
              >
                <Icon variant="copy" size="xs" color="black_90" />
              </Event>
            )}
          </div>
        ) : (
          value
        )}
      </div>
      <div className="description__actions">{extra}</div>
    </div>
  );
};

export const Description = memo(DescriptionComponent);

export declare namespace DescriptionGroupType {
  type Props = {
    className?: string | null;
    children?:
      | React.ReactElement<DescriptionType.Props>
      | React.ReactElement<DescriptionType.Props>[]
      | null;
    title?: string | null;
    subtitle?: string | null;
    extra?: React.ReactNode | React.ReactNode[];
    expandable?: boolean | null;
    expandableIcon?: IconType.Props['variant'];
    disabled?: boolean | null;
    isSubGroup?: boolean | null;
    backgroundColor?: boolean | null;
  } & (
    | {
        variant?: 'split';
        columns?: number;
      }
    | {
        variant?: 'cover';
      }
  );
}

export const DescriptionGroup = memo(
  ({
    children,
    className,
    title,
    subtitle,
    extra,
    expandable = false,
    expandableIcon = 'edit',
    variant = 'split',
    disabled = false,
    isSubGroup = false,
    backgroundColor = true,
    ...props
  }: DescriptionGroupType.Props) => {
    const id = useId();
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const { isMobile } = useDevice();

    const columns = useMemo(() => {
      return variant === 'split'
        ? (props as { columns?: number }).columns || 2
        : 1;
    }, [variant, props]);

    return (
      <div
        key={id}
        className={classNames(`descriptionGroup`, className, {
          'descriptionGroup--disabled': disabled,
        })}
        style={{
          gridTemplateRows:
            ((title && !expandable) || (expandable && isExpanded)) && !!children
              ? 'min-content 1fr'
              : '1fr',
          height: variant === 'cover' ? '100%' : undefined,
          backgroundColor: backgroundColor
            ? getCSS(`--color-${isSubGroup ? 'black_2' : 'black_4'}`)
            : 'transparent',
          padding: !isSubGroup
            ? isMobile
              ? `${remCSS(16)} ${remCSS(24)}`
              : `${remCSS(24)} ${remCSS(32)}`
            : `${remCSS(16)} ${remCSS(16)}`,
        }}
      >
        {(title || subtitle || extra || expandable) && (
          <div
            className={classNames(`descriptionGroup__header`, {
              'descriptionGroup__header--expandable': expandable,
            })}
          >
            <div className="descriptionGroup__header__present">
              {title && (
                <Text
                  whiteSpace="nowrap"
                  variant={isSubGroup ? 'button-m' : 'headline'}
                  color="black_90"
                >
                  {title}
                </Text>
              )}
              {subtitle && (
                <Text variant="caption" color="black_50">
                  {subtitle}
                </Text>
              )}
            </div>

            {extra}

            {expandable && !isExpanded && (
              <ButtonRounded
                variant="quaternary"
                icon={expandableIcon}
                onClick={() => setIsExpanded(true)}
              />
            )}
          </div>
        )}

        {(!expandable || (expandable && isExpanded)) && children && (
          <div
            className="descriptionGroup__contain"
            style={{
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              gridTemplateRows: variant === 'cover' ? '1fr' : undefined,
              overflowY: variant === 'cover' ? 'auto' : undefined,
            }}
          >
            {React.Children.map(children, (child) => {
              if (
                child &&
                React.isValidElement(child) &&
                child.type === DescriptionGroup
              ) {
                return React.cloneElement<DescriptionGroupType.Props>(child, {
                  ...child.props,
                  isSubGroup: true,
                });
              }

              return React.cloneElement(child, {});
            })}
          </div>
        )}
      </div>
    );
  },
);

DescriptionGroup.displayName = 'DescriptionGroup';
